import React, { useState } from 'react'
import cx from 'classnames'
import { Accordion, Table } from '@scentregroup/ui'

import { useSiteConfiguration } from '../../../lib/site-configuration'
import trackMkpEvents, { labels } from '../../../lib/market-place-track-event'
import {
  IMarketplaceProductVariant,
  MarketplaceProduct,
  IMarketplaceProductInformationEntry,
  IMarketplaceProductInformationText,
} from '../../../types/marketplace-product'
import { getAccordionReturnableContent } from './returnable-content'

import style from '../index.module.scss'
import { Markdown } from '@scentregroup/ui'
import { Text } from '@hub/text'
import { Box } from '@hub/box'
import { IMarketplaceProductPromotion } from '@scentregroup/shared/types'
import useFeatureFlag from '@scentregroup/shared/hooks/use-feature-flag'
import { FeatureFlagName } from '@scentregroup/shared/feature-flags'

/**
 * An Accordion that contains product details,
 * some details are optional and only display if present,
 * others are not.
 */

interface IProductDetailsAccordion {
  product: MarketplaceProduct
  selectedVariant: IMarketplaceProductVariant | null
  expandTermsAndConditions?: boolean
  returnable: boolean | undefined
  applicablePromotions?: IMarketplaceProductPromotion[]
}

const columns = [
  {
    key: 'name',
    heading: '',
  },
  {
    key: 'info',
    heading: '',
  },
]

const isHTMLSanitised = (item: IMarketplaceProductInformationText): boolean =>
  ['Description', 'Long Description', 'Short Description'].includes(item.label)

const accordionHasValues = (
  information: IMarketplaceProductInformationEntry
): boolean =>
  information && (information['entries']?.length > 0 || information['text'])

const ProductDetailsAccordion: React.FC<
  React.PropsWithChildren<IProductDetailsAccordion>
> = ({
  product,
  selectedVariant,
  expandTermsAndConditions,
  returnable,
  applicablePromotions,
}) => {
  const variant = selectedVariant ?? product.variants[0]

  const directReturnPolicyHref =
    useSiteConfiguration().marketplace.urls.directReturnPolicy

  const enableBrandUpdate = useFeatureFlag(
    FeatureFlagName.NEXT_PUBLIC_FEAT_FLAG_UPDATED_BRAND
  )

  return (
    <>
      {variant?.information.map(information => {
        return (
          accordionHasValues(information) && (
            <AccordionItem key={information.label} label={information.label}>
              {'entries' in information ? (
                <Table
                  className={style.specificationTable}
                  columns={columns}
                  rows={information.entries
                    ?.filter(entry => entry.label && entry.text)
                    .map(entry => ({
                      name: entry.label,
                      info: entry.text,
                    }))}
                />
              ) : information.label === 'Returns' ? (
                getAccordionReturnableContent(
                  returnable,
                  enableBrandUpdate,
                  directReturnPolicyHref
                )
              ) : isHTMLSanitised(information) ? (
                <div dangerouslySetInnerHTML={{ __html: information.text }} />
              ) : (
                <p>{information.text}</p>
              )}
            </AccordionItem>
          )
        )
      })}
      {applicablePromotions?.some(promo => promo.directPromotion !== null) && (
        <AccordionItem
          label="Promotion T&Cs"
          isOnlyFirstLetterUppercase={false}
          initiallyExpanded={expandTermsAndConditions}
        >
          {applicablePromotions.map(promotion => {
            if (promotion.directPromotion) {
              return (
                <Box key={promotion.title}>
                  <Text tone="strong">{promotion.title}</Text>
                  <Markdown
                    content={promotion.directPromotion.termsAndConditions}
                    wrapperClass={style.termsAndConditionsAccordion}
                  />
                </Box>
              )
            }
            return
          })}
        </AccordionItem>
      )}
    </>
  )
}
/**
 * a light wrapper to handle the accordion items styling
 */
const AccordionItem: React.FC<
  React.PropsWithChildren<{
    label: string
    sublabel?: string
    isOnlyFirstLetterUppercase?: boolean
    initiallyExpanded?: boolean
  }>
> = ({
  label,
  sublabel,
  isOnlyFirstLetterUppercase = true,
  initiallyExpanded,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  return (
    <Accordion
      label={label}
      sublabel={sublabel}
      type="small"
      classNames={{
        wrapper: style.accordionWrapper,
        header: cx({
          [style.accordionContentHeader]: isExpanded,
        }),
        content: style.accordionContent,
        label: isOnlyFirstLetterUppercase
          ? style.accordionLabel
          : style.accordionLabelWithUpperCase,
        sublabel: style.accordionSublabel,
      }}
      initiallyExpanded={initiallyExpanded}
      onClick={(isExpanded: boolean) => {
        trackMkpEvents.expanded(labels.createInfoAccordion(label, isExpanded))
        setIsExpanded(isExpanded)
      }}
    >
      {children}
    </Accordion>
  )
}

export default ProductDetailsAccordion
